/**
 * Homepage coordination for OGT rebuild sections.
 * - Offer builder (#s-offer / #ogt-offer-data)
 * - Cart add from offer CTA (variant + optional selling_plan → cart drawer)
 * - Carousel arrows (.ogt-car-nav)
 * - Deep links (.ogt-sub-link / .ogt-ot-link → plan + scroll; data-ogt-range)
 *
 * No-ops when required DOM is missing. Pricing comes only from
 * <script type="application/json" id="ogt-offer-data"> (no prototype fallback).
 */
(function () {
  'use strict';

  window.OGT = window.OGT || {};

  var S = (window.OGT.state = {
    plan: 'sub',
    range: 'o2',
    qty: 0,
    flavour: 0
  });
  var DATA = null;
  var submitting = false;

  function loadThemeModule(name) {
    if (typeof window.__ogtLoadThemeModule === 'function') {
      return window.__ogtLoadThemeModule(name);
    }
    return Promise.resolve();
  }

  function waitFor(predicate, tries) {
    tries = tries == null ? 40 : tries;
    return new Promise(function (resolve) {
      (function tick(left) {
        if (predicate() || left <= 0) return resolve();
        requestAnimationFrame(function () {
          tick(left - 1);
        });
      })(tries);
    });
  }

  function ensureCartModules() {
    var chain = Promise.resolve();
    if (!customElements.get('site-drawers')) {
      chain = chain.then(function () {
        return loadThemeModule('drawers');
      });
    }
    if (!customElements.get('shopify-cart')) {
      chain = chain.then(function () {
        return loadThemeModule('shopify-cart');
      });
    }
    return chain
      .then(function () {
        return customElements.whenDefined('site-drawers');
      })
      .then(function () {
        return customElements.whenDefined('shopify-cart');
      })
      .then(function () {
        return waitFor(function () {
          return !!(window.drawers && document.querySelector('shopify-cart'));
        });
      });
  }

  function refreshCartDrawer() {
    var cart = document.querySelector('shopify-cart');
    if (
      !cart ||
      typeof cart.render !== 'function' ||
      typeof cart.getSectionsToRender !== 'function'
    ) {
      return Promise.resolve(false);
    }

    var sections = cart.getSectionsToRender().map(function (section) {
      return section.section;
    });
    var sectionUrl = window.location.pathname + '?sections=' + sections.join(',');
    cart.loading = true;

    var statePromise =
      typeof cart.getCart === 'function' ? cart.getCart() : Promise.resolve(null);

    return statePromise
      .then(function (state) {
        if (state) cart.cartState = state;
        return fetch(sectionUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        }).then(function (res) {
          return res.json();
        });
      })
      .then(function (renderedSections) {
        return cart.render({ sections: renderedSections });
      })
      .then(function () {
        return true;
      })
      .catch(function () {
        cart.loading = false;
        return false;
      });
  }

  function openCartSidebar() {
    return ensureCartModules()
      .then(function () {
        if (window.drawers) {
          window.drawers.activeDrawer = 'cart';
        } else {
          var trigger = document.querySelector('[data-drawer-trigger="cart"]');
          if (!trigger) return false;
          trigger.click();
        }
        return refreshCartDrawer().then(function () {
          return true;
        });
      })
      .catch(function () {
        return false;
      });
  }

  window.OGT.openCartSidebar = openCartSidebar;

  function readOfferData() {
    var el = document.getElementById('ogt-offer-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function moneyAmount(v) {
    return (Math.round((Number(v) + Number.EPSILON) * 100) / 100).toFixed(2);
  }

  function currencySymbol() {
    if (window.theme && typeof window.theme.getCurrencySymbol === 'function') {
      return window.theme.getCurrencySymbol();
    }
    if (window.theme && window.theme.currencySymbol) return window.theme.currencySymbol;
    return '£';
  }

  function money(v) {
    return currencySymbol() + moneyAmount(v);
  }

  function per(price, bars) {
    return currencySymbol() + (Number(price) / bars).toFixed(2);
  }

  function q() {
    return DATA[S.range].qty[S.qty];
  }

  function freqKey() {
    var freq = document.getElementById('freq');
    if (!freq) return '4';
    return String(freq.value || '4').indexOf('2') === 0 ? '2' : '4';
  }

  function resolveSellingPlanId(tier, range) {
    var plans = (tier && tier.plans) || (range && range.plans) || {};
    return plans[freqKey()] || plans['4'] || plans['2'] || (range && range.sellingPlanId) || null;
  }

  /* ---- chip press state (any [data-group] / .ogt-chip) ---- */
  function initChipGroups(root) {
    (root || document).querySelectorAll('[data-group]').forEach(function (g) {
      if (g.__ogtBound) return;
      g.__ogtBound = true;
      g.addEventListener('click', function (e) {
        var b = e.target.closest('.ogt-chip');
        if (!b) return;
        g.querySelectorAll('.ogt-chip').forEach(function (c) {
          c.setAttribute('aria-pressed', 'false');
        });
        b.setAttribute('aria-pressed', 'true');
      });
    });
  }

  /* ---- carousel arrows ---- */
  function initCarousels(root) {
    (root || document).querySelectorAll('.ogt-car-nav').forEach(function (nav) {
      if (nav.__ogtBound) return;
      var car = document.getElementById(nav.dataset.for);
      if (!car) return;
      nav.__ogtBound = true;
      nav.addEventListener('click', function (e) {
        var b = e.target.closest('.ogt-car-btn');
        if (!b) return;
        var slide = car.querySelector('.ogt-slide');
        if (!slide) return;
        var cs = getComputedStyle(car);
        var gap = parseFloat(cs.columnGap || cs.gap) || 16;
        car.scrollBy({
          left: (slide.getBoundingClientRect().width + gap) * (+b.dataset.dir),
          behavior: 'smooth'
        });
      });
    });
  }

  function flavourMeta(f) {
    if (!f) return { name: '', tray: 'variety', image: '', variantId: '', sub: 0, ot: 0, plans: null };
    if (typeof f === 'string') {
      var parts = f.split('|');
      return {
        name: parts[0] || '',
        tray: parts[1] || 'variety',
        image: parts[2] || '',
        variantId: '',
        sub: 0,
        ot: 0,
        plans: null
      };
    }
    return {
      name: f.name || '',
      tray: f.tray || 'variety',
      image: f.image || '',
      images: f.images || [],
      variantId: f.variantId || '',
      sub: Number(f.sub) || 0,
      ot: Number(f.ot) || 0,
      plans: f.plans || null
    };
  }

  function varietyIndex(range) {
    var list = (range && range.flavours) || [];
    for (var i = 0; i < list.length; i++) {
      if (flavourMeta(list[i]).tray === 'variety') return i;
    }
    return 0;
  }

  /* Untouched offer → variety pack of the chosen range. A flavour chip the
     shopper actually pressed keeps that product. */
  function currentFlavour(range) {
    var list = (range && range.flavours) || [];
    if (!list.length) return flavourMeta(null);
    var idx = S.flavour;
    if (idx < 0 || idx >= list.length) idx = varietyIndex(range);
    return flavourMeta(list[idx]);
  }

  function boxCount(tier) {
    if (!tier) return 1;
    return tier.quantity || Math.max(1, Math.round((tier.bars || 12) / 12));
  }

  /* Variety uses the range tier prices (including 24/36 discounts). A single
     flavour uses that product's 12-bar price times the chosen box count. */
  function lineValue(range, tier) {
    var flavour = currentFlavour(range);
    var boxes = boxCount(tier);
    if (flavour.tray !== 'variety' && flavour.variantId) {
      var unit = S.plan === 'sub' ? flavour.sub : flavour.ot;
      if (unit > 0) return unit * boxes;
    }
    return S.plan === 'sub' ? tier.sub : tier.ot;
  }

  function setGalleryImage(src, className) {
    var gi = document.getElementById('ogalImg');
    if (!gi || !src) return;
    var current = gi.getAttribute('src') || '';
    if (className) gi.className = className;
    if (current === src || current.indexOf(src) !== -1) {
      gi.style.opacity = 1;
      return;
    }
    gi.style.opacity = 0;
    setTimeout(function () {
      gi.src = src;
      gi.style.opacity = 1;
    }, 180);
  }

  // The chosen flavour's photos under the main picture (range product pages, 27 Oct 2026). Rebuilds the row only
  // when the flavour changes; returns the photo that should be showing, or '' when the row is not on the page.
  /* product page layout: the small photos as one row; with more than fit, the last is cut off and the brand
     arrows scroll it a photo at a time (Dan, 27 Oct). Arrows show only when there is more to see that way. */
  function pdpStrip(box) {
    var wrap = box.closest('.ogt-pdpstrip');
    if (!wrap) return;
    var per = Number(wrap.getAttribute('data-per')) || 5;
    var many = box.children.length > per;
    if (many) box.setAttribute('data-many', '');
    else box.removeAttribute('data-many');
    var prev = wrap.querySelector('.ogt-pdpstrip__btn--prev');
    var next = wrap.querySelector('.ogt-pdpstrip__btn--next');
    var update = function () {
      if (prev) prev.hidden = !many || box.scrollLeft < 4;
      if (next) next.hidden = !many || box.scrollLeft + box.clientWidth >= box.scrollWidth - 4;
    };
    if (!wrap.__ogtBound) {
      wrap.__ogtBound = true;
      var step = function (dir) {
        var t = box.querySelector('.ogt-ogal__thumb');
        box.scrollBy({ left: dir * (t ? t.getBoundingClientRect().width + 8 : 80), behavior: 'smooth' });
        setTimeout(function () { wrap.__ogtUpdate(); }, 450);
      };
      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });
      box.addEventListener('scroll', function () { wrap.__ogtUpdate(); }, { passive: true });
      window.addEventListener('resize', function () { wrap.__ogtUpdate(); });
    }
    wrap.__ogtUpdate = update;
    update();
  }

  function renderFlavourThumbs(meta) {
    var box = document.getElementById('ogalThumbs');
    var imgs = (meta && meta.images) || [];
    if (!box || !imgs.length) {
      if (box) box.hidden = true;
      return '';
    }
    var key = imgs.map(function (m) { return m.src; }).join('|');
    if (box.dataset.key !== key) {
      box.dataset.key = key;
      box.innerHTML = '';
      box.scrollLeft = 0;
      imgs.forEach(function (m, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'ogt-ogal__thumb';
        b.setAttribute('aria-pressed', String(i === 0));
        b.setAttribute('aria-label', m.alt || 'Photo ' + (i + 1));
        var im = document.createElement('img');
        im.src = m.thumb || m.src;
        im.alt = '';
        im.loading = 'lazy';
        b.appendChild(im);
        b.addEventListener('click', function () {
          Array.prototype.forEach.call(box.children, function (c) { c.setAttribute('aria-pressed', String(c === b)); });
          setGalleryImage(m.src);
        });
        box.appendChild(b);
      });
    }
    box.hidden = imgs.length < 2;
    pdpStrip(box);
    var on = box.querySelector('[aria-pressed="true"]');
    var idx = on ? Array.prototype.indexOf.call(box.children, on) : 0;
    return (imgs[idx] || imgs[0]).src;
  }

  /* ---- offer builder ---- */
  function paintTrays(tray) {
    document.querySelectorAll('.ogt-oconf .ogt-minitray').forEach(function (mt) {
      if (tray === 'variety') {
        mt.classList.add('variety');
        mt.style.removeProperty('--tray');
      } else {
        mt.classList.remove('variety');
        mt.style.setProperty('--tray', tray);
      }
    });
  }

  function updateSaveNote() {
    var n = document.getElementById('saveNote');
    var freq = document.getElementById('freq');
    if (!n || !freq || !DATA || !DATA[S.range]) return;
    var d = DATA[S.range];
    var base = d.qty[0];
    var k = q();
    var packs = k.bars / 12;
    var introSave = packs * (base.ot - base.sub);
    var onSave = packs * (base.ot - base.subOn);
    var wk = freq.value.indexOf('2') === 0 ? 2 : 4;
    var boxes = Math.floor(52 / wk);
    var yr = introSave * 3 + (boxes - 3) * onSave;
    n.textContent =
      'Save ' +
      currencySymbol() +
      moneyAmount(introSave) +
      ' on each of your first three boxes, then ' +
      currencySymbol() +
      moneyAmount(onSave) +
      ' every box after — around ' +
      currencySymbol() +
      moneyAmount(yr) +
      ' in your first year.';
  }

  function benefitLines(raw) {
    return String(raw || '')
      .split(/\r?\n/)
      .map(function (l) {
        return l.trim();
      })
      .filter(Boolean);
  }

  function renderPlanBenefitLists() {
    if (!DATA || !DATA[S.range]) return;
    var d = DATA[S.range];
    var iconTpl = document.getElementById('ogtPlanTickIcon');
    var subList = document.getElementById('subBenefitsList');
    var otList = document.getElementById('otBenefitsList');

    if (subList) {
      var subLines = benefitLines(d.subBenefits);
      var frag = document.createDocumentFragment();
      var hasOngoing = false;
      subLines.forEach(function (st) {
        var li = document.createElement('li');
        if (iconTpl) li.appendChild(iconTpl.content.cloneNode(true));
        if (st.indexOf('{ongoing}') !== -1) {
          hasOngoing = true;
          var span = document.createElement('span');
          span.id = 'ongoing';
          span.textContent = d.ongoing || '';
          li.appendChild(span);
        } else {
          li.appendChild(document.createTextNode(st));
        }
        frag.appendChild(li);
      });
      if (!hasOngoing) {
        var liOn = document.createElement('li');
        if (iconTpl) liOn.appendChild(iconTpl.content.cloneNode(true));
        var spanOn = document.createElement('span');
        spanOn.id = 'ongoing';
        spanOn.textContent = d.ongoing || '';
        liOn.appendChild(spanOn);
        frag.appendChild(liOn);
      }
      subList.replaceChildren(frag);
    }

    if (otList) {
      var otLines = benefitLines(d.otBenefits);
      var otFrag = document.createDocumentFragment();
      otLines.forEach(function (ot) {
        var li = document.createElement('li');
        var x = document.createElement('span');
        x.className = 'ogt-xm';
        x.setAttribute('aria-hidden', 'true');
        x.textContent = '✕';
        li.appendChild(x);
        li.appendChild(document.createTextNode(ot));
        otFrag.appendChild(li);
      });
      otList.replaceChildren(otFrag);
      otList.hidden = otLines.length === 0;
    }
  }

  function renderGalleryPanel() {
    if (!DATA || !DATA[S.range]) return;
    var d = DATA[S.range];
    var gt = document.getElementById('ogalTag');
    var ge = document.getElementById('ogalEdu');
    var ticks = document.getElementById('ogalTicks');
    var iconTpl = document.getElementById('ogtGalTickIcon');
    var gi = document.getElementById('ogalImg');

    if (gt && typeof d.galTag === 'string') gt.textContent = d.galTag;
    if (ge) {
      var h = ge.querySelector('h4');
      var p = ge.querySelector('p');
      if (h && typeof d.galHeading === 'string') h.textContent = d.galHeading;
      if (p && typeof d.galBody === 'string') p.textContent = d.galBody;
    }
    if (gi && d.galCls) gi.className = d.galCls;

    if (ticks) {
      var lines = benefitLines(d.galTicks);
      var frag = document.createDocumentFragment();
      lines.forEach(function (t) {
        var li = document.createElement('li');
        if (iconTpl) li.appendChild(iconTpl.content.cloneNode(true));
        li.appendChild(document.createTextNode(t));
        frag.appendChild(li);
      });
      ticks.replaceChildren(frag);
      ticks.hidden = lines.length === 0;
    }
  }

  function refreshOffer() {
    var offer = document.getElementById('s-offer');
    if (!offer || !DATA || !DATA[S.range]) return;
    var d = DATA[S.range];
    var k = q();

    /* range buttons: "£1.63/bar" is the subscription price a bar, and the one-time price a bar while buying once
       (Dan, 27 Oct). Only notes the theme worked out itself (data-live); a typed note stays as typed. */
    offer.querySelectorAll('[data-ranges] .ogt-chip').forEach(function (rc) {
      var note = rc.querySelector('.ogt-chip__note[data-live]');
      var rd = DATA[rc.dataset.range];
      var t12 = rd && rd.qty && rd.qty[0];
      if (!note || !t12) return;
      var v = S.plan === 'sub' ? t12.sub : t12.ot;
      if (v > 0) note.textContent = money(v / (t12.bars || 12)) + '/bar';
    });

    var subPrice = document.getElementById('subPrice');
    var subWas = document.getElementById('subWas');
    var subUnit = document.getElementById('subUnit');
    var otPrice = document.getElementById('otPrice');
    var otUnit = document.getElementById('otUnit');
    if (subPrice) subPrice.textContent = money(k.sub);
    if (subWas) subWas.textContent = money(k.ot);
    if (subUnit) subUnit.textContent = per(k.sub, k.bars) + ' per bar';
    if (otPrice) otPrice.textContent = money(k.ot);
    if (otUnit) otUnit.textContent = per(k.ot, k.bars) + ' per bar';
    renderPlanBenefitLists();
    renderGalleryPanel();

    if (!d.flavours || S.flavour < 0 || S.flavour >= d.flavours.length) {
      S.flavour = varietyIndex(d);
    }

    var cta = offer.querySelector('.ogt-offerb__cta');
    if (cta && !cta.disabled) {
      var shown = lineValue(d, k);
      cta.textContent =
        S.plan === 'sub'
          ? 'Start my subscription — ' + money(shown)
          : 'Add to basket — ' + money(shown);
    }
    var pdp = offer.classList.contains('ogt-offerb--pdp');
    var fg = offer.querySelector('[data-flavours]');
    if (fg) {
      fg.innerHTML = d.flavours
        .map(function (f, i) {
          var meta = flavourMeta(f);
          return (
            '<button type="button" class="ogt-chip" aria-pressed="' +
            (i === S.flavour) +
            '" data-tray="' +
            meta.tray +
            '"' +
            (meta.image ? ' data-gal-img="' + meta.image + '"' : '') +
            '>' +
            (pdp ? flavourDot(d, meta) : '') +
            meta.name +
            '</button>'
          );
        })
        .join('');
    }
    if (pdp) refreshPdp(offer, d, k);

    var oconf = offer.querySelector('.ogt-oconf');
    if (oconf) {
      oconf.className = oconf.className
        .split(/\s+/)
        .filter(function (c) {
          return c && c.indexOf('range-') !== 0;
        })
        .join(' ');
      if (S.range) oconf.classList.add('range-' + S.range);
    }

    var chips = offer.querySelectorAll(
      '[data-group]:not([data-ranges]):not([data-flavours]) .ogt-chip'
    );
    d.qty.forEach(function (tk, i) {
      var chip = chips[i];
      if (!chip) return;
      var price = S.plan === 'sub' ? tk.sub : tk.ot;
      var save = S.plan === 'sub' ? 0 : (tk.bars / 12) * d.qty[0].ot - tk.ot;
      var lbl =
        (i === 0 ? '12 bars' : String(tk.bars)) +
        ' — ' +
        money(price).replace('.00', '') +
        (save > 0.005 ? ' · save ' + currencySymbol() + moneyAmount(save) : '');
      var trays = tk.bars / 12;
      var tray = '';
      for (var t = 0; t < trays; t++) {
        tray +=
          '<span class="ogt-minitray variety" aria-hidden="true">' +
          new Array(13).join('<i></i>') +
          '</span>';
      }
      if (offer.classList.contains('ogt-offerb--pdp')) {
        /* product page layout: "save £x" on its own small line, so each button keeps one line of price */
        /* every quantity in the 12-bar format, "24 bars — £42" (Dan, 27 Oct) */
        var main = tk.bars + ' bars — ' + money(price).replace('.00', '');
        lbl = '<span class="ogt-qlbl">' + main +
          (save > 0.005 ? '<small>save ' + currencySymbol() + moneyAmount(save) + '</small>' : '') + '</span>';
      }
      chip.innerHTML = tray + lbl;
      chip.setAttribute('aria-pressed', String(i === S.qty));
    });

    // After qty chips are rebuilt — keep tray + gallery on the selected flavour
    var curFlavour = flavourMeta(d.flavours[S.flavour]);
    paintTrays(curFlavour.tray);
    var thumbSrc = renderFlavourThumbs(curFlavour);
    if (thumbSrc) setGalleryImage(thumbSrc);
    else if (curFlavour.image) setGalleryImage(curFlavour.image);
    else if (d.galImage) setGalleryImage(d.galImage);

    var tl = offer.querySelector('.ogt-trustlines--c');
    if (tl) tl.style.display = S.plan === 'sub' ? '' : 'none';

    syncBuybar();
    updateSaveNote();
  }

  /* ---- product page layout (range product pages, 27 Oct 2026; only when the module has
     .ogt-offerb--pdp, so the homepage is untouched) ---- */
  function flavourDot(range, meta) {
    var bg = meta.tray;
    if (meta.tray === 'variety') {
      var cols = range.flavours
        .map(flavourMeta)
        .filter(function (m) { return m.tray !== 'variety'; })
        .map(function (m) { return m.tray; });
      var step = 360 / Math.max(1, cols.length);
      bg = 'conic-gradient(' + cols.map(function (c, i) {
        return c + ' ' + (i * step) + 'deg ' + ((i + 1) * step) + 'deg';
      }).join(', ') + ')';
    }
    return '<span class="ogt-fdot" aria-hidden="true" style="background:' + bg + '"></span>';
  }

  /* the ingredients lists, once: each headed with its flavour's name, and the product's own allergy line
     swapped for the section's (as on the canvases) */
  function pdpDetailsOnce(offer) {
    if (offer.__ogtPdpDetails) return;
    offer.__ogtPdpDetails = true;
    var allergy = document.getElementById('ogtPdpAllergy');
    offer.querySelectorAll('.ogt-pdpd__ingr').forEach(function (box) {
      var name = box.getAttribute('data-name') || '';
      var paras = box.querySelectorAll('p');
      if (allergy) {
        paras.forEach(function (p) {
          if (/^\s*allergy advice/i.test(p.textContent)) p.parentNode.removeChild(p);
        });
        /* the product data often keeps it in the ingredients paragraph, after two line breaks:
           cut from the line breaks before "ALLERGY ADVICE" to the end of that paragraph */
        box.querySelectorAll('strong, b').forEach(function (st) {
          if (!/^\s*allergy advice/i.test(st.textContent) || !st.parentNode) return;
          var start = st;
          while (start.previousSibling && (start.previousSibling.nodeName === 'BR' || !start.previousSibling.textContent.trim())) {
            start = start.previousSibling;
          }
          var n = start;
          while (n) { var next = n.nextSibling; n.parentNode.removeChild(n); n = next; }
        });
      }
      var first = box.querySelector('p');
      if (first && name) {
        var b = document.createElement('b');
        b.textContent = name + ':';
        first.insertBefore(document.createTextNode(' '), first.firstChild);
        first.insertBefore(b, first.firstChild);
      }
      if (allergy) box.appendChild(allergy.content.cloneNode(true));
    });
  }

  function planValue(range, tier, plan) {
    var keep = S.plan;
    S.plan = plan;
    var v = lineValue(range, tier);
    S.plan = keep;
    return v;
  }

  function refreshPdp(offer, d, k) {
    var cur = d.flavours[S.flavour];
    var meta = flavourMeta(cur);
    var nm = document.getElementById('ogtFlavName');
    if (nm) nm.textContent = meta.name;
    /* Buying once, as on the canvases: the subscription card stays, the main button reads "Or start
       subscription" (and switches back), and the one-time card with its own button opens underneath. */
    var subV = planValue(d, k, 'sub');
    var otV = planValue(d, k, 'ot');
    var cta = offer.querySelector('.ogt-offerb__cta');
    var saveTag = offer.querySelector('.ogt-pdph__save');
    if (cta && !cta.disabled) {
      cta.innerHTML =
        (saveTag ? '<span class="ogt-flag ogt-pdph__ctaflag" aria-hidden="true">' + saveTag.textContent + '</span>' : '') +
        (S.plan === 'sub' ? 'Start subscription · ' : 'Or start subscription · ') + money(subV);
    }
    /* the subscribe card's price line: "12 bars · all three flavours" left, "£1.75 / bar" right */
    var nFlav = d.flavours.map(flavourMeta).filter(function (m) { return m.tray !== 'variety'; }).length;
    var NUM = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight'];
    var barsLine = k.bars + ' bars' + (meta.tray === 'variety' ? ' · all ' + (NUM[nFlav] || nFlav) + ' flavours' : '');
    var su = document.getElementById('subUnit');
    if (su) su.innerHTML = '<span>' + barsLine + '</span><b>' + money(subV / k.bars) + ' / bar</b>';
    pdpDetailsOnce(offer);
    var sw = document.getElementById('ogtPlanSwitch');
    if (sw) {
      var txt = sw.getAttribute('data-text-ot') || '';
      sw.textContent = S.plan === 'sub' ? txt.replace('[price]', money(otV)) : '';
      sw.setAttribute('data-to', 'ot');
    }
    var pot = document.getElementById('ogtPdpOt');
    if (pot) {
      pot.hidden = S.plan !== 'ot';
      var set = function (id, t) { var el = document.getElementById(id); if (el) el.textContent = t; };
      var others = d.flavours.map(flavourMeta).filter(function (m) { return m.tray !== 'variety'; }).length;
      set('ogtPdpOtWas', money(subV));
      set('ogtPdpOtAmt', money(otV));
      set('ogtPdpOtUnit', barsLine);
      set('ogtPdpOtSubBar', money(subV / k.bars));
      set('ogtPdpOtBar', money(otV / k.bars) + ' / bar');
      set('ogtPdpOtAdd', 'Add to basket · ' + money(otV));
    }
    /* the dropdowns follow a single flavour; Variety leaves them where they are */
    var handle = cur && cur.handle;
    if (handle && meta.tray !== 'variety') {
      offer.querySelectorAll('.ogt-pdpd [data-pick-flavour][data-handle="' + handle + '"]').forEach(function (c) {
        if (c.getAttribute('aria-pressed') !== 'true') c.click();
      });
    }
  }

  function offerCta() {
    return document.getElementById('ogtOfferCta');
  }

  function buybarCta() {
    var bar = buybarEl();
    return bar ? bar.querySelector('.ogt-bb-cta') : null;
  }

  function setBuybarLabel(label) {
    var btn = buybarCta();
    if (!btn || !label) return;
    var slot = btn.querySelector('.ogt-bb-label');
    if (slot) slot.textContent = label;
  }

  function setCtaBusy(cta, busy, label) {
    submitting = busy;
    if (cta) {
      cta.disabled = !!busy;
      if (label) cta.textContent = label;
    }
    var bb = buybarCta();
    if (bb) {
      bb.disabled = !!busy;
      if (busy && label) setBuybarLabel(label);
    }
  }

  function cartEndpoints() {
    var src = offerCta() || buybarCta();
    return {
      addUrl:
        (src && src.getAttribute('data-cart-add')) ||
        (window.routes && window.routes.cart_add_url) ||
        '/cart/add',
      cartUrl:
        (src && src.getAttribute('data-cart-url')) ||
        (window.routes && window.routes.cart_url) ||
        '/cart'
    };
  }

  function addOfferToCart() {
    var cta = offerCta();
    if (submitting || !DATA || !DATA[S.range]) return;

    var range = DATA[S.range];
    var tier = q();
    var flavour = currentFlavour(range);
    var useFlavour = flavour.tray !== 'variety' && flavour.variantId;
    var variantId = useFlavour ? flavour.variantId : tier.variantId || range.variantId;
    if (!variantId) {
      setCtaBusy(cta, false, 'Unavailable — pick a product in the theme editor');
      setBuybarLabel('Unavailable');
      return;
    }

    var quantity = boxCount(tier);
    var payload = {
      items: [
        {
          id: Number(variantId),
          quantity: Number(quantity)
        }
      ]
    };

    if (S.plan === 'sub') {
      var planId = useFlavour
        ? resolveSellingPlanId(flavour, flavour)
        : resolveSellingPlanId(tier, range);
      if (!planId) {
        setCtaBusy(cta, false, 'Subscription unavailable — try one-time');
        setBuybarLabel('Unavailable');
        return;
      }
      payload.items[0].selling_plan = Number(planId);
    }

    var ends = cartEndpoints();
    var shown = lineValue(range, tier);
    var idleLabel =
      S.plan === 'sub'
        ? 'Start my subscription — ' + money(shown)
        : 'Add to basket — ' + money(shown);

    setCtaBusy(cta, true, 'Adding…');

    fetch(ends.addUrl + '.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) {
            throw err;
          });
        }
        return openCartSidebar().then(function (opened) {
          setCtaBusy(cta, false, idleLabel);
          refreshOffer();
          if (!opened && ends.cartUrl) {
            window.location.href = ends.cartUrl;
          }
        });
      })
      .catch(function () {
        setCtaBusy(cta, false, idleLabel);
        if (cta) cta.textContent = 'Couldn’t add — try again';
        setBuybarLabel('Try again');
        setTimeout(function () {
          if (!submitting) refreshOffer();
        }, 2200);
      });
  }

  function pickRange(rangeKey) {
    if (!rangeKey || !DATA || !DATA[rangeKey]) return;
    S.range = rangeKey;
    S.flavour = varietyIndex(DATA[rangeKey]);
    var chip = document.querySelector('[data-ranges] .ogt-chip[data-range="' + rangeKey + '"]');
    if (chip) {
      var group = chip.closest('[data-group]');
      if (group) {
        group.querySelectorAll('.ogt-chip').forEach(function (c) {
          c.setAttribute('aria-pressed', String(c === chip));
        });
      }
      chip.click();
    } else {
      refreshOffer();
    }
  }

  /* ---- sticky buy bar ----
     sections/ogt-buy-bar.liquid renders #buybar and carries every control for
     it. Built 31 Aug 2026: the bar existed in the 2026 prototype and its DOM
     contract was documented, but no section ever rendered it AND this half of
     the script was missing from the repo too - it survived only in the retired
     Dan Sandbox copy, which 889ee0e replaced wholesale. Same class of gap as
     the header shrink that #101 restored.

     Deliberate change from the prototype: the visible-state class is
     .ogt-bb-raised, not the bare `show` the prototype used. `show` is a banned
     class word on this theme - the compiled CSS matches state words by
     substring, which is what once rendered a whole section as a floating
     overlay. Nothing was live on `show`, so the section and this file were
     changed together and no behaviour was broken.

     Everything the bar needs is read off data- attributes on the bar itself,
     so the section owns the settings and this file never needs editing again.
     All lower-case, one word each: dataset turns data-bb-x into bbX, which is
     the trap the rulebook records against data-bbrange.

     WHY NO IntersectionObserver, when the prototype and the Sandbox script both
     used one to watch #s-hero: an observer is bound to the exact element that
     existed when initBuybar() ran. This theme renders parts of a page lazily
     (snippets/ogt-lazy-home-modules.liquid), and the theme editor re-runs
     initAll() on shopify:section:load - so if the hero node is ever replaced,
     the observer is left watching a node no longer in the document and the bar
     never appears again, silently. Reading getBoundingClientRect() on scroll
     looks the element up fresh every time, so it cannot go stale. It also made
     the behaviour testable from here: a headless pane paints no frames, and an
     observer delivers nothing without frames, so the observer version could not
     be verified at all. Found 31 Aug 2026 while building this. */
  var buybarBound = false;

  function buybarEl() {
    return document.getElementById('buybar');
  }

  /* The width a CSS media query would use. NOT window.innerWidth: that is the
     visual viewport, which drifts from the layout viewport when a phone is
     pinch-zoomed and reports the outer window in some embedded contexts. Using
     clientWidth means the wording and the layout change on exactly the same
     pixel. Found 1 Sep 2026 - the phone wording did not switch because
     innerWidth was reporting the host window, not the page. */
  function buybarViewportWidth() {
    return document.documentElement.clientWidth || window.innerWidth || 0;
  }
  /* Is this a phone? Asked of matchMedia so the script and the stylesheet can
     never disagree about the breakpoint - they evaluate the same query. Falls
     back to clientWidth, never innerWidth, which misreports on real devices.
     RESTORED 2 Sep 2026: deleted by accident while removing a dead function
     next to it, which broke the diagnostic immediately. */
  function buybarNarrow(bar) {
    var bp = parseInt(bar.dataset.bbbreak, 10) || 880;
    if (window.matchMedia) {
      return window.matchMedia('(max-width: ' + bp + 'px)').matches;
    }
    return buybarViewportWidth() <= bp;
  }

  function buybarSetShown(bar, on) {
    if (!bar || bar.__ogtBbShown === on) return;
    bar.__ogtBbShown = on;
    window.OGT.__bbHasShown = on;
    bar.classList.toggle('ogt-bb-raised', on);
    bar.setAttribute('aria-hidden', String(!on));
    /* keeps the CTA out of the tab order while the bar is off screen */
    if (on) bar.removeAttribute('inert');
    else bar.setAttribute('inert', '');
    /* Klaviyo's hub button sits fixed 10px off the bottom of the screen, which
       is inside this bar, and it covered a 42x41px corner of the Subscribe
       button - about a third of it - on 375, 390 and 430px screens alike. A tap
       near the CTA's right edge hit Klaviyo's icon instead of the button. It
       places itself in absolute pixels and offers no way to declare an
       obstacle, so the bar has to publish its own height and let the stylesheet
       move it - see .kl-hub-launcher-container in custom.css.liquid.
       offsetHeight is 0 while the bar is switched off for this breakpoint,
       which zeroes the lift on its own. */
    var lift = bar.offsetHeight;
    document.documentElement.style.setProperty(
      '--ogt-bb-lift',
      on && lift > 0 ? lift + 8 + 'px' : '0px'
    );
    /* A fixed bar covers the last ~60px of the page permanently, so the end of
       the footer becomes unreachable, and nothing inside the section's own
       boundary can reserve that space. Padding only - never display, position
       or flex, which is what reordered the whole page on 7 Aug 2026. The
       section's "Push the page content up" control switches it off. */
    if (bar.dataset.bbpad === '1') {
      var h = bar.offsetHeight;
      document.body.style.paddingBottom = on && h > 0 ? h + 8 + 'px' : '';
    }
  }

  /* True once this element has scrolled off the top of the screen, or has not
     arrived below the fold yet. NOT an IntersectionObserver, on purpose - see
     the note at the top of this block. `offset` reproduces the -80px top
     rootMargin the prototype used on the hero. */
  /* Reported by Dan on a phone, 1 Sep 2026: "the sticky bar appears too late and
     as soon as you scroll up it disappears. It should the same as it does on
     desktop." Both symptoms, one cause - the reveal waited for the hero to
     scroll fully past, and ogt-hero-split is about 3200px tall on a phone
     against 1070px on desktop. So it appeared three screens down instead of
     one, and because it appeared exactly AT the hero's bottom edge, scrolling
     up by a few pixels put the hero back on screen and hid it again.

     Two changes:
     1. The hero gate ARMS the bar rather than continuously gating it. Once the
        bar has earned its first appearance it stays up until you come back near
        the top of the page. Nudging the scroll wheel can no longer flick it
        away. (The offer-module gate stays continuous - hiding there is its
        whole purpose.)
     2. On a phone the hero gate is off by default, because no rule based on a
        3200px element can appear at a sensible moment. Phones use the scroll
        distance alone, defaulting to roughly one screen. */
  /* THE BUG BEHIND EVERY FAILED ATTEMPT AT MAKING THE BAR HOLD, found by Dan on
     2 Sep 2026: "as soon as the email popup appeared after 10 seconds or so, it
     stopped sticking".

     Klaviyo locks page scrolling when its popup opens - it puts
     `klaviyo-prevent-body-scrolling` on <body>. The usual way to lock scrolling
     is position:fixed with a negative top, or overflow:hidden after resetting the
     offset, and BOTH make window.pageYOffset report 0 while the reader is still
     halfway down the page. The keep-it-visible rule then saw "we are at the top"
     and put the bar away. Shopify's own cookie banner and any other modal do the
     same thing.

     So: while the page is locked, do not evaluate at all - leave the bar exactly
     as the reader last saw it. Nothing about a popup should change it.

     Only overflowY is tested, never the shorthand or overflowX: this theme sets
     overflow-x:hidden on <body> permanently, so checking that would report a lock
     on every page and freeze the bar for good. */
  function buybarScrollLocked() {
    var b = document.body;
    if (!b) return false;
    var bs = getComputedStyle(b);
    if (bs.position === 'fixed') return true;
    if (bs.overflowY === 'hidden') return true;
    var hs = getComputedStyle(document.documentElement);
    if (hs.overflowY === 'hidden') return true;
    return false;
  }

  /* REMOVED 2 Sep 2026: buybarCorrectDrift(). It measured where the bar had
     landed and nudged it back if a transformed ancestor had displaced it. That
     was a guess at a problem never actually observed on this store, and it
     caused a real one: it compared the bar's rectangle against
     window.innerHeight, which on Dan's phone reports 1568 while the visible
     area is roughly half that. It therefore saw a 768px error that did not
     exist and pushed the bar clean off the bottom of the screen on every
     scroll event - "it appears in the correct place but disappears as soon as I
     scroll up". The class was right the whole time; the transform was wrong.

     THE LESSON: never ship a speculative correction for an unobserved fault.
     And never trust window.innerHeight as the visible height - it disagreed with
     the layout viewport by a factor of two on a real device.

     If a transformed ancestor ever does displace the bar, fix it then, against
     a measurement from the device where it happens. */
  /* REWRITTEN 2 Sep 2026 after a full audit, because five separate patches had
     each fixed a symptom and left the cause standing. Two rules, and BOTH read
     the watched section's rectangle - never window.pageYOffset.

     WHY THAT IS THE WHOLE POINT: a popup that locks page scrolling makes
     pageYOffset unreliable, while getBoundingClientRect stays correct under
     every locking technique. Every earlier version keyed off the scroll position
     somewhere, which is why the bar kept dying when the email popup appeared.

     ALSO REMOVED: the "do not evaluate while the page is locked" guard from v10.
     Klaviyo locks by putting overflow:hidden on <body> through its own
     klaviyo-prevent-body-scrolling class AND LEAVES THE CLASS THERE, so that
     guard never released and the bar froze for good. It was the immediate cause
     of Dan's "No, it does not" on 2 Sep. buybarScrollLocked() is kept only for
     the diagnostic - it must never gate behaviour again. */
  /* REBUILT FROM SCRATCH 2 Sep 2026, on Dan's instruction, after fifteen
     versions that each computed this from live geometry and each failed on his
     phone. Every one of those inputs proved unreliable on a real device:

       window.innerHeight   reported 1568 on an iPhone SE whose viewport is 667,
                            and 0 in an embedded pane
       section rectangles   move as images load and as sections re-render
       element flags        wiped by hot reload and by shopify:section:load
       overflow:hidden      set permanently by Klaviyo, so useless as a signal

     So it now uses TWO NUMBERS DAN SETS HIMSELF and nothing else. It appears
     once you have scrolled past the first, and it holds until you come back
     above the second. Both are plain scroll positions in pixels, both are in the
     theme editor, and there is nothing left that a browser can misreport.

     The only input is the scroll position, and that one was verified as reliable
     even with Klaviyo's popup open - it read 2012 correctly while the page was
     locked. */
  /* Dan picks a section from a dropdown and the bar arrives at the end of it.
     Pixel numbers remain the fallback for a page with no named section.

     WHY SECTION-BASED RULES KEPT FAILING TO HOLD - sixteen versions to see it:
     A SECTION IS A MOVING TARGET. As images load and lazy content arrives, every
     section slides further down the page. A disappear rule recomputed from the
     section's live position therefore drifts downwards past where the reader
     already is, and the bar retracts on its own with no scrolling at all. That is
     why it 'did not hold' however the rule was phrased.

     So: the ARRIVAL point is measured live, which has always worked. But the
     instant the bar appears, the DISAPPEAR point is frozen as a plain document
     position - the top of that same section, as it stood at that moment. Nothing
     can move it afterwards. Stored on window so a re-render cannot lose it. */
  /* Layout viewport height - same source as buybarViewportWidth(). Never use
     window.innerHeight: it has misreported by ~2x on real phones. */
  function buybarViewportHeight() {
    return document.documentElement.clientHeight || window.innerHeight || 0;
  }

  /* Appears the moment the bottom of the chosen section reaches the bottom of
     the screen - i.e. straight after that section, not a full viewport later.
     (The old rule waited until the section bottom hit the TOP of the screen,
     which on desktop put the bar halfway into the standards table.)

     Stays up while scrolling down or back up; only hides when the reader
     scrolls up far enough that the section bottom drops below the fold again.
     Hiding is gated on scroll direction so layout drift cannot retract it. */
  function buybarEvaluate() {
    var bar = buybarEl();
    if (!bar) return;

    if (bar.dataset.bbmode === 'always') {
      buybarSetShown(bar, true);
      return;
    }

    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    var lastY = window.OGT.__bbLastScrollY;
    if (typeof lastY !== 'number') lastY = y;
    var scrollingUp = y < lastY - 1;
    window.OGT.__bbLastScrollY = y;

    var narrow = buybarNarrow(bar);
    var sectionId = (narrow ? bar.dataset.bbsectionm : bar.dataset.bbsection) || '';
    var el = sectionId ? document.getElementById(sectionId) : null;

    if (el) {
      var r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return;
      var early = parseInt(narrow ? bar.dataset.bbearlym : bar.dataset.bbearly, 10);
      if (isNaN(early)) early = 0;
      /* Positive early brings the bar forward (larger threshold → fires sooner). */
      var threshold = buybarViewportHeight() + early;
      var pastEnd = r.bottom <= threshold;
      if (pastEnd) {
        buybarSetShown(bar, true);
      } else if (bar.__ogtBbShown && scrollingUp) {
        buybarSetShown(bar, false);
      }
      return;
    }

    var appearAt = parseInt(narrow ? bar.dataset.bbappearm : bar.dataset.bbappear, 10);
    if (isNaN(appearAt)) appearAt = 600;
    if (y >= appearAt) {
      buybarSetShown(bar, true);
    } else if (bar.__ogtBbShown && scrollingUp && y < appearAt) {
      buybarSetShown(bar, false);
    }
  }

  /* A one-word diagnostic, because working out why the bar is or is not showing
     took several rounds of guessing on 1 Sep 2026 and cost Dan a lot of patience.
     He types OGT.buybar() in the console and gets a plain-English answer, including
     the script version - the single most common cause was testing a cached copy
     after a change. Bump BUYBAR_VERSION whenever the reveal logic changes. */
  var BUYBAR_VERSION = 'v21 - appear at section bottom vs viewport bottom, 2 Sep 2026';

  window.OGT.buybar = function () {
    var bar = buybarEl();
    if (!bar) return { version: BUYBAR_VERSION, problem: 'No buy bar on this page.' };
    var narrow = buybarNarrow(bar);
    var sectionId = (narrow ? bar.dataset.bbsectionm : bar.dataset.bbsection) || '';
    var el = sectionId ? document.getElementById(sectionId) : null;
    var y = Math.round(window.pageYOffset || document.documentElement.scrollTop || 0);
    var r = el ? el.getBoundingClientRect() : null;
    var earlyVal = (function () {
      var e = parseInt(narrow ? bar.dataset.bbearlym : bar.dataset.bbearly, 10);
      return isNaN(e) ? 0 : e;
    })();
    var vh = buybarViewportHeight();
    return {
      version: BUYBAR_VERSION,
      showingNow: bar.classList.contains('ogt-bb-raised'),
      rule: 'Appears when the bottom of the chosen section reaches the bottom of the screen. Hides only when you scroll back up past that point.',
      arrivesAtEndOfSection: sectionId || '(using the pixel number instead)',
      thatSectionFound: !!el,
      yourScrollPositionNow: y,
      thatSectionEndsAt: r ? Math.round(y + r.bottom) : null,
      broughtForwardBy: earlyVal,
      soItArrivesAt: r ? Math.round(y + r.bottom - vh - earlyVal) : null,
      hidesWhenScrollingUpTo: r ? Math.round(y + r.bottom - vh - earlyVal) : null,
      treatedAs: narrow ? 'phone' : 'desktop',
      screenWidth: buybarViewportWidth(),
      barWidth: Math.round(bar.getBoundingClientRect().width),
      buttonOnScreen: (function () {
        var a = bar.querySelector('.ogt-bb-cta');
        return a ? a.getBoundingClientRect().right <= buybarViewportWidth() + 1 : null;
      })()
    };
  };

  function initBuybar() {
    var bar = buybarEl();
    if (!bar) return;

    if (bar.dataset.bbpad !== '1') document.body.style.paddingBottom = '';

    /* One scroll/resize listener for the life of the page, not one per section
       reload - the theme editor re-runs initAll() on every save. */
    if (!buybarBound) {
      buybarBound = true;
      addEventListener('scroll', buybarEvaluate, { passive: true });
      /* resize re-runs syncBuybar too, because the wording and the reveal
         distance both depend on the width */
      addEventListener('resize', function () {
        buybarEvaluate();
        syncBuybar();
      }, { passive: true });
    }

    if (!bar.__ogtBound) {
      bar.__ogtBound = true;

      /* A fresh element after a re-render starts with no idea it was ever up.
         Hand it the class straight away so there is no frame in which the
         appear rule can pull it back down. */
      if (window.OGT.__bbHasShown) {
        bar.__ogtBbShown = true;
        bar.classList.add('ogt-bb-raised');
        bar.setAttribute('aria-hidden', 'false');
        bar.removeAttribute('inert');
      }

      var pills = bar.querySelector('.ogt-bb-ranges');
      if (pills) {
        pills.addEventListener('click', function (e) {
          var p = e.target.closest('.ogt-bb-pill');
          if (!p) return;
          pickRange(p.dataset.bbrange);
        });
      }

      var cta = bar.querySelector('.ogt-bb-cta');
      if (cta) {
        cta.addEventListener('click', function (e) {
          if (DATA && DATA[S.range]) {
            e.preventDefault();
            addOfferToCart();
            return;
          }
          var href = cta.getAttribute('data-bbfallback');
          if (href && href.charAt(0) === '#' && smoothScrollTo(href)) {
            e.preventDefault();
          } else if (href && cta.tagName === 'BUTTON') {
            e.preventDefault();
            window.location.href = href;
          }
        });
      }
    }

    buybarEvaluate();
    syncBuybar();
  }

  /* range pages: a flavour name that will not fit on one line beside the button runs inline with the price; if
     even that needs more than two lines, the name stops at the last whole word that fits, "Himalayan Salted…",
     never on "&" (Dan, 27 Oct) */
  function pdpBuybarFit(bar) {
    var nm = document.getElementById('bbName');
    var pr = document.getElementById('bbPrice');
    var box = nm && nm.parentElement;
    if (!box) return;
    var full = nm.dataset.full || nm.textContent;
    nm.textContent = full;
    nm.removeAttribute('title');
    box.classList.remove('ogt-bb-text--flow');
    var tooWide = function () {
      nm.style.whiteSpace = 'nowrap';
      var w = box.clientWidth > 0 && nm.scrollWidth > box.clientWidth + 1;
      nm.style.whiteSpace = '';
      return w;
    };
    if (tooWide()) {
      box.classList.add('ogt-bb-text--flow');
      var tops = {};
      [nm, pr].forEach(function (el) {
        if (!el) return;
        Array.prototype.forEach.call(el.getClientRects(), function (r) { tops[Math.round(r.top)] = 1; });
      });
      if (Object.keys(tops).length > 2) {
        box.classList.remove('ogt-bb-text--flow');
        var words = full.split(/\s+/);
        for (var n = words.length - 1; n > 0; n--) {
          var cut = words.slice(0, n);
          while (cut.length && cut[cut.length - 1] === '&') cut.pop();
          if (!cut.length) break;
          nm.textContent = cut.join(' ') + '…';
          if (!tooWide()) break;
        }
        nm.title = full;
      }
    }
    if (!bar.__ogtFitBound) {
      bar.__ogtFitBound = true;
      window.addEventListener('resize', function () { pdpBuybarFit(bar); });
    }
  }

  /* Called from refreshOffer(), so the bar always shows the offer module's
     current range, quantity and plan. No-ops on a page with no offer module,
     where the bar keeps whatever the theme editor's own text settings say. */
  function syncBuybar() {
    var bar = buybarEl();
    if (!bar) return;

    bar.querySelectorAll('.ogt-bb-pill').forEach(function (p) {
      /* "wp1"/"wp2" pills belong to a welcome pack page's range choice (assets/ogt-welcome-pack.js) */
      if ((p.dataset.bbrange || '').indexOf('wp') === 0) return;
      p.setAttribute('aria-pressed', String(p.dataset.bbrange === S.range));
    });

    if (!DATA || !DATA[S.range]) return;
    var d = DATA[S.range];
    var k = d.qty && d.qty[S.qty];
    if (!k) return;

    /* Range product pages (Dan, 27 Oct, third mockup): flavour name on the first line, "£1.75/bar · 12 bars" in
       regular weight under it; a name too long for one line runs inline instead (bold name · price · bars) so the
       bar stays two lines. Button: "Subscribe · £21.00 £30" (full price struck) or, buying once,
       "Buy now · £21 £30.00" (subscription price struck, as on the one-time card). */
    if (document.querySelector('.ogt-offerb--pdp')) {
      var fm = currentFlavour(d);
      var subV = planValue(d, k, 'sub');
      var otV = planValue(d, k, 'ot');
      var curV = S.plan === 'sub' ? subV : otV;
      var nmP = document.getElementById('bbName');
      var prP = document.getElementById('bbPrice');
      if (nmP) { nmP.dataset.full = fm.name; nmP.textContent = fm.name; }
      if (prP) prP.textContent = money(curV / k.bars) + '/bar · ' + k.bars + ' bars';
      pdpBuybarFit(bar);
      var btnP = bar.querySelector('.ogt-bb-cta');
      if (btnP && !submitting) {
        btnP.innerHTML = S.plan === 'sub'
          ? '<span class="ogt-bb-label">Subscribe · ' + money(subV) + '</span> <s>' + money(planValue(d, d.qty[0], 'ot') * boxCount(k)).replace('.00', '') + '</s>'
          : '<span class="ogt-bb-label">Buy now ·</span> <s>' + money(subV).replace('.00', '') + '</s> ' + money(otV);
      }
      return;
    }

    var unit = bar.dataset.bbunit || '';
    var perWord = bar.dataset.bbper || '';
    /* "subscribed" needs 148px at the widest price and a phone has about 134px,
       so the section carries a shorter phone wording. Same breakpoint the CSS
       uses, so the words change on exactly the same line the layout does. */
    var narrow = buybarViewportWidth() <= (parseInt(bar.dataset.bbbreak, 10) || 880);
    var suffix = (narrow ? bar.dataset.bbsuffixm : bar.dataset.bbsuffix) || '';

    var value = lineValue(d, k);
    var total = money(value).replace('.00', '');
    if (S.plan === 'sub' && suffix) total += ' ' + suffix;
    var perBar = per(value, k.bars) + perWord;
    var qty = k.bars + (unit ? ' ' + unit : '');

    var nm = document.getElementById('bbName');
    var pr = document.getElementById('bbPrice');

    /* Dan, 1 Sep 2026: on a phone the range name is already in the highlighted
       pill immediately to its left, so repeating it wastes the one line that is
       short of room. His arrangement: per-bar price and quantity on the top line,
       the total and the word underneath. Measured at 113px against 115px of room,
       and every real combination is the same 19 characters wide, so they all fit.
       Desktop keeps the full version - it has the room and it reads better. */
    if (buybarNarrow(bar) && bar.dataset.bblayoutm === 'compact') {
      if (nm) nm.textContent = perBar + ' \u00b7 ' + qty;
      if (pr) pr.textContent = total;
    } else {
      if (nm) nm.textContent = d.name + ' \u00b7 ' + qty;
      if (pr) pr.textContent = total + ' \u00b7 ' + perBar;
    }

    var btn = bar.querySelector('.ogt-btn');
    if (btn && !submitting) {
      var label =
        S.plan === 'sub'
          ? bar.dataset.bblabelsub
          : bar.dataset.bblabelot || bar.dataset.bblabelsub;
      if (label) (btn.querySelector('.ogt-bb-label') || btn).textContent = label;
    }
  }

  function initOffer() {
    var offer = document.getElementById('s-offer');
    if (!offer || !DATA) return;

    var planGroup = document.getElementById('planGroup');
    if (planGroup && !planGroup.__ogtBound) {
      planGroup.__ogtBound = true;
      planGroup.addEventListener('click', function (e) {
        if (e.target.closest('.ogt-freqrow')) return;
        var b = e.target.closest('.ogt-plan');
        if (!b) return;
        S.plan = b.dataset.plan;
        planGroup.querySelectorAll('.ogt-plan').forEach(function (p) {
          p.setAttribute('aria-checked', String(p === b));
        });
        refreshOffer();
      });
      planGroup.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          var other = planGroup.querySelector('.ogt-plan[aria-checked="false"]');
          if (other) {
            other.click();
            other.focus();
          }
          e.preventDefault();
        }
        if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('.ogt-freqrow')) {
          var pl = e.target.closest('.ogt-plan');
          if (pl) pl.click();
          e.preventDefault();
        }
      });
    }

    var freq = document.getElementById('freq');
    if (freq && !freq.__ogtBound) {
      freq.__ogtBound = true;
      freq.addEventListener('change', updateSaveNote);
    }

    /* product page layout: the two frequency buttons drive the #freq select */
    var pills = offer.querySelector('.ogt-freqpills');
    if (pills && freq && !pills.__ogtBound) {
      pills.__ogtBound = true;
      var syncPills = function () {
        pills.querySelectorAll('[data-freq]').forEach(function (p) {
          p.setAttribute('aria-pressed', String(p.getAttribute('data-freq') === freq.value));
        });
      };
      pills.addEventListener('click', function (e) {
        var p = e.target.closest('[data-freq]');
        if (!p) return;
        freq.value = p.getAttribute('data-freq');
        freq.dispatchEvent(new Event('change', { bubbles: true }));
        syncPills();
      });
      freq.addEventListener('change', syncPills);
      syncPills();
    }

    /* product page layout: "Or buy once" / "Or subscribe" switches plan through the plan cards */
    var planSwitch = document.getElementById('ogtPlanSwitch');
    if (planSwitch && planGroup && !planSwitch.__ogtBound) {
      planSwitch.__ogtBound = true;
      planSwitch.addEventListener('click', function () {
        var to = planSwitch.getAttribute('data-to');
        var card = planGroup.querySelector('.ogt-plan[data-plan="' + to + '"]');
        if (card) card.click();
      });
    }

    var ranges = offer.querySelector('[data-ranges]');
    if (ranges && !ranges.__ogtRangeBound) {
      ranges.__ogtRangeBound = true;
      ranges.addEventListener('click', function (e) {
        var b = e.target.closest('.ogt-chip');
        if (!b) return;
        S.range = b.dataset.range;
        S.flavour = varietyIndex(DATA[S.range]);
        refreshOffer();
      });
    }

    offer.querySelectorAll('[data-flavours]').forEach(function (g) {
      if (g.__ogtFlavBound) return;
      g.__ogtFlavBound = true;
      g.addEventListener('click', function (e) {
        var b = e.target.closest('.ogt-chip');
        if (!b || !b.dataset.tray) return;
        if (g.closest('.ogt-oconf')) {
          S.flavour = Array.prototype.indexOf.call(g.children, b);
          refreshOffer();
          return;
        }
        paintTrays(b.dataset.tray);
        if (b.dataset.galImg) setGalleryImage(b.dataset.galImg);
      });
    });

    offer.querySelectorAll('[data-group]').forEach(function (g) {
      if (g.hasAttribute('data-ranges') || g.hasAttribute('data-flavours') || g.__ogtQtyBound) {
        return;
      }
      g.__ogtQtyBound = true;
      g.addEventListener('click', function (e) {
        var b = e.target.closest('.ogt-chip');
        if (!b) return;
        S.qty = Array.prototype.indexOf.call(g.children, b);
        var m = document.getElementById('traymult');
        if (m) m.textContent = S.qty === 1 ? '×2' : S.qty === 2 ? '×3' : '';
        refreshOffer();
      });
    });

    var cta = document.getElementById('ogtOfferCta');
    if (cta && !cta.__ogtBound) {
      cta.__ogtBound = true;
      cta.addEventListener('click', function (e) {
        e.preventDefault();
        /* product page layout: while buying once, the main button reads "Or start subscription" and switches back */
        if (offer.classList.contains('ogt-offerb--pdp') && S.plan === 'ot' && planGroup) {
          var subCard = planGroup.querySelector('.ogt-plan[data-plan="sub"]');
          if (subCard) subCard.click();
          return;
        }
        addOfferToCart();
      });
    }
    var otAdd = document.getElementById('ogtPdpOtAdd');
    if (otAdd && !otAdd.__ogtBound) {
      otAdd.__ogtBound = true;
      otAdd.addEventListener('click', function (e) {
        e.preventDefault();
        addOfferToCart();
      });
    }

    if (!DATA[S.range]) {
      S.range = Object.keys(DATA)[0] || S.range;
    }

    /* Range product pages: start on the flavour of the product being viewed, so a flavour clicked on a collection
       page (/products/salted-caramel-box) opens with that flavour chosen, not Variety (Dan, 27 Oct). A ?flavour=
       product handle in the address does the same. */
    if (!offer.__ogtStartFlavour) {
      offer.__ogtStartFlavour = true;
      var want = '';
      try { want = new URLSearchParams(window.location.search).get('flavour') || ''; } catch (err) { want = ''; }
      want = want || offer.getAttribute('data-pdp-handle') || '';
      if (want) {
        Object.keys(DATA).some(function (rk) {
          var list = DATA[rk].flavours || [];
          for (var i = 0; i < list.length; i++) {
            if (list[i] && list[i].handle === want) {
              S.range = rk;
              S.flavour = i;
              return true;
            }
          }
          return false;
        });
      }
    }

    refreshOffer();
  }

  /* ---- deep links → pick plan + smooth-scroll ---- */
  function smoothScrollTo(hash) {
    if (!hash || hash.charAt(0) !== '#' || hash.length < 2) return false;
    var id = hash.slice(1).split('?')[0];
    var target = document.getElementById(id);
    if (!target) return false;
    var reduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    return true;
  }

  function initDeepLinks(root) {
    var pickPlan = function (sel) {
      var el = document.querySelector(sel);
      if (el && el.getAttribute('aria-checked') !== 'true') el.click();
    };
    var bind = function (selector, planSel) {
      (root || document).querySelectorAll(selector).forEach(function (a) {
        if (a.__ogtBound) return;
        a.__ogtBound = true;
        a.addEventListener('click', function (e) {
          if (a.getAttribute('data-ogt-range')) pickRange(a.getAttribute('data-ogt-range'));
          if (planSel) pickPlan(planSel);
          var href = a.getAttribute('href');
          if (href && smoothScrollTo(href)) e.preventDefault();
        });
      });
    };
    bind('.ogt-ot-link', '.ogt-plan--ot');
    /* .ogt-hdr-cta is the theme header's button. sections/ogt-header.liquid
       documents itself against this: "the button. ogt-page.js pre-selects the
       Subscribe plan". Restored 26 Aug 2026 - it was lost when this file was
       taken wholesale from staging in 889ee0e.
       The sticky bar button is NOT in this list: it adds the current selection
       to the basket (initBuybar), it does not scroll to the offer module. */
    bind('.ogt-sub-link, .ogt-hdr-cta', '.ogt-plan--sub');
    /* Range-only deep links (e.g. goal tiles with plan = none) */
    bind('a[data-ogt-range]:not(.ogt-sub-link):not(.ogt-ot-link)', null);
  }

  /* ---- FAQ accordion height animation ---- */
  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function faqPanelOf(details) {
    return details.querySelector('.ogt-faq__panel');
  }

  function faqSetHeight(panel, px) {
    panel.style.height = typeof px === 'number' ? px + 'px' : px;
  }

  function faqAnimateClose(details, done) {
    var panel = faqPanelOf(details);
    if (!panel) {
      details.open = false;
      if (done) done();
      return;
    }
    if (details.__ogtFaqAnimating) return;
    details.__ogtFaqAnimating = true;
    details.classList.add('is-closing');

    var start = panel.scrollHeight;
    faqSetHeight(panel, start);
    panel.offsetHeight;

    var finished = false;
    var finish = function () {
      if (finished) return;
      finished = true;
      details.__ogtFaqAnimating = false;
      details.open = false;
      details.classList.remove('is-closing');
      faqSetHeight(panel, 0);
      panel.removeEventListener('transitionend', onEnd);
      if (done) done();
    };
    var onEnd = function (e) {
      if (e && e.target !== panel) return;
      finish();
    };

    if (prefersReducedMotion()) {
      finish();
      return;
    }
    requestAnimationFrame(function () {
      faqSetHeight(panel, 0);
    });
    panel.addEventListener('transitionend', onEnd);
    setTimeout(finish, 400);
  }

  function faqAnimateOpen(details) {
    var panel = faqPanelOf(details);
    if (!panel) {
      details.open = true;
      return;
    }
    if (details.__ogtFaqAnimating) return;
    details.__ogtFaqAnimating = true;

    details.open = true;
    faqSetHeight(panel, 0);
    panel.offsetHeight;
    var end = panel.scrollHeight;

    var finished = false;
    var finish = function () {
      if (finished) return;
      finished = true;
      details.__ogtFaqAnimating = false;
      faqSetHeight(panel, 'auto');
      panel.removeEventListener('transitionend', onEnd);
    };
    var onEnd = function (e) {
      if (e && e.target !== panel) return;
      finish();
    };

    if (prefersReducedMotion()) {
      faqSetHeight(panel, 'auto');
      details.__ogtFaqAnimating = false;
      return;
    }
    requestAnimationFrame(function () {
      faqSetHeight(panel, end);
    });
    panel.addEventListener('transitionend', onEnd);
    setTimeout(finish, 400);
  }

  function initFaqAccordions(root) {
    (root || document).querySelectorAll('.ogt-faq__list').forEach(function (list) {
      if (list.__ogtFaqBound) return;
      list.__ogtFaqBound = true;
      list.classList.add('ogt-faq__list--animated');

      list.querySelectorAll('details.ogt-faq__item').forEach(function (details) {
        var panel = faqPanelOf(details);
        var summary = details.querySelector('summary');
        if (!panel || !summary) return;

        if (details.open) {
          faqSetHeight(panel, 'auto');
        } else {
          faqSetHeight(panel, 0);
        }

        summary.addEventListener('click', function (e) {
          e.preventDefault();
          if (details.__ogtFaqAnimating) return;

          if (details.open) {
            faqAnimateClose(details);
            return;
          }

          var name = details.getAttribute('name');
          if (name) {
            var sel = 'details.ogt-faq__item[open]';
            list.querySelectorAll(sel).forEach(function (other) {
              if (other !== details && other.getAttribute('name') === name) {
                faqAnimateClose(other);
              }
            });
          }

          faqAnimateOpen(details);
        });
      });
    });
  }

  /* ---- header collapse on scroll ----
     sections/ogt-header.liquid styles `.ogt-hdr.shrunk` and carries seven
     "Shrunk ..." controls that do nothing without this. The class it adds is
     the BARE, UNPREFIXED `shrunk` - the section's own comment says so, so do
     not rename it. Restored 26 Aug 2026; lost when this file was taken
     wholesale from staging in 889ee0e, which flagged the follow-up in its
     commit message and never did it. */
  function initHeader() {
    var hdr = document.querySelector('.ogt-hdr');
    if (!hdr || hdr.__ogtBound) return;
    hdr.__ogtBound = true;
    addEventListener(
      'scroll',
      function () {
        hdr.classList.toggle('shrunk', scrollY > 24);
      },
      { passive: true }
    );
  }

  /* ---- boot ---- */
  function initAll(root) {
    DATA = readOfferData();
    initChipGroups(root);
    initCarousels(root);
    initOffer();
    initBuybar();
    initDeepLinks(root);
    initFaqAccordions(root);
    initHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
